import { createContext, useContext, useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Tabs from "@/components/base/Tabs";
import { useToast } from "@/components/base/Toast";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { addTeacher, createTeacherAccount, deleteTeacher, getTeachers, updateTeacher } from "@/services/hrService";
import type { Cycle, SchoolClass, Subject, Teacher } from "@/types";
import TeacherFormModal from "@/pages/dashboard/teachers/components/TeacherFormModal";
import {
  createEmptyForm,
  teacherToForm,
  type TeacherFormState,
} from "@/pages/dashboard/teachers/components/teacherForm";
import DeleteTeacherModal from "@/pages/dashboard/teachers/components/DeleteTeacherModal";
import TeacherHoursTab from "@/pages/dashboard/teachers/components/TeacherHoursTab";
import Modal from "@/components/base/Modal";
import { Input } from "@/components/base/Field";
import TemporaryPasswordDialog, { type TemporaryAccount } from "@/components/feature/TemporaryPasswordDialog";
import { cycleIdOf } from "@/utils/cycleScope";
import { isRated } from "@/utils/payroll";
import { getClasses, getSubjects } from "@/services/academicService";
import { getCycles } from "@/services/schoolService";
import { EMPLOYMENT_STATUS_LABEL } from "@/utils/hr";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { useAuth } from "@/contexts/AuthContext";
import { listUsers } from "@/services/userApi";

/* ------------------------------------------------------------------------- */
/* Données de référence (cycles, classes, matières) partagées par la page     */
/* ------------------------------------------------------------------------- */

interface TeacherLabels {
  primaryCycleId: string;
  secondaryCycleId: string;
  cycleLabels: Record<string, string>;
  classShortById: Record<string, string>;
  classNameById: Record<string, string>;
  subjectNameById: Record<string, string>;
}

function buildLabels(cycles: Cycle[], classes: SchoolClass[], subjects: Subject[]): TeacherLabels {
  return {
    primaryCycleId: cycleIdOf(cycles, "PRIMAIRE"),
    secondaryCycleId: cycleIdOf(cycles, "SECONDAIRE"),
    cycleLabels: Object.fromEntries(cycles.map((cycle) => [cycle.id, cycle.label])),
    classShortById: Object.fromEntries(classes.map((cls) => [cls.id, cls.shortName ?? cls.name])),
    classNameById: Object.fromEntries(classes.map((cls) => [cls.id, cls.name])),
    subjectNameById: Object.fromEntries(subjects.map((subject) => [subject.id, subject.name])),
  };
}

const LabelsContext = createContext<TeacherLabels>(buildLabels([], [], []));

function useLabels(): TeacherLabels {
  return useContext(LabelsContext);
}

/* ------------------------------------------------------------------------- */
/* Sous-composants réutilisables                                              */
/* ------------------------------------------------------------------------- */

function TeacherAvatar({ teacher }: { teacher: Teacher }) {
  const isPrimary = teacher.cycleId === useLabels().primaryCycleId;
  const initials = `${teacher.firstName.charAt(0)}${teacher.lastName.charAt(0)}`.toUpperCase();
  if (teacher.photo) {
    return (
      <img
        src={teacher.photo}
        alt={`${teacher.firstName} ${teacher.lastName}`}
        title={`${teacher.firstName} ${teacher.lastName}`}
        className="h-9 w-9 shrink-0 rounded-full border border-background-200 object-cover object-top"
      />
    );
  }
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        isPrimary ? "bg-warning-100 text-warning-800" : "bg-secondary-100 text-secondary-800"
      }`}
    >
      {initials}
    </span>
  );
}

function ContactCell({ teacher }: { teacher: Teacher }) {
  return (
    <div>
      <p className="text-xs text-foreground-700">{teacher.phone}</p>
      <p className="text-xs text-foreground-400">{teacher.email}</p>
    </div>
  );
}

const statusStyles: Record<Teacher["status"], string> = {
  ACTIVE: "bg-primary-100 text-primary-700",
  ON_LEAVE: "bg-accent-100 text-accent-700",
  INACTIVE: "bg-accent-50 text-accent-600",
};

function StatusCell({ teacher }: { teacher: Teacher }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[teacher.status]}`}
    >
      {EMPLOYMENT_STATUS_LABEL[teacher.status]}
    </span>
  );
}

/* ------------------------------------------------------------------------- */
/* Tableaux                                                                   */
/* ------------------------------------------------------------------------- */

interface TableProps {
  /** Absent : l'utilisateur ne peut pas créer de compte. */
  onCreateAccount?: (teacher: Teacher) => void;
  rows: Teacher[];
  cycleLabel: string;
  /** Filtres actifs : la pagination revient à la page 1 quand ils changent. */
  filterKey: string;
  onEdit: (teacher: Teacher) => void;
  onDelete: (teacher: Teacher) => void;
}

function PrimaryTable({ rows, filterKey, onEdit, onDelete, onCreateAccount }: TableProps) {
  const page = usePager().slice(rows, filterKey);
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full bg-primary-600"></span>
        <h4 className="text-sm font-semibold font-heading text-foreground-900">Cycle Primaire</h4>
        <Badge tone="primary">{rows.length} enseignants</Badge>
      </div>

      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="border-b border-background-100 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                <th className="px-5 py-3 font-medium">Enseignant</th>
                <th className="px-5 py-3 font-medium">Classe</th>
                <th className="px-5 py-3 font-medium">Contact</th>
                <th className="px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((teacher) => (
                <tr
                  key={teacher.id}
                  className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <TeacherAvatar teacher={teacher} />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">
                          {teacher.firstName} {teacher.lastName}
                        </p>
                        <p className="text-xs text-foreground-500">
                          Depuis {teacher.hireDate.slice(0, 4)} · Primaire
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <ClassChip classId={teacher.primaryClassId} />
                  </td>
                  <td className="px-5 py-3.5">
                    <ContactCell teacher={teacher} />
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusCell teacher={teacher} />
                  </td>
                  <td className="px-5 py-3.5">
                    <RowActions teacher={teacher} onEdit={onEdit} onDelete={onDelete} onCreateAccount={onCreateAccount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-100" />
      </div>
    </section>
  );
}

function SecondaryTable({ rows, filterKey, onEdit, onDelete, onCreateAccount }: TableProps) {
  const page = usePager().slice(rows, filterKey);
  const { classShortById, subjectNameById } = useLabels();
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span>
        <h4 className="text-sm font-semibold font-heading text-foreground-900">Cycle Secondaire</h4>
        <Badge tone="accent">{rows.length} enseignants</Badge>
      </div>

      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-background-100 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                <th className="px-5 py-3 font-medium">Enseignant</th>
                <th className="px-5 py-3 font-medium">Affectations</th>
                <th className="px-5 py-3 font-medium">Contact</th>
                <th className="px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((teacher) => (
                <tr
                  key={teacher.id}
                  className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <TeacherAvatar teacher={teacher} />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">
                          {teacher.firstName} {teacher.lastName}
                        </p>
                        <p className="text-xs text-foreground-500">
                          Depuis {teacher.hireDate.slice(0, 4)} · Secondaire
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex max-w-[280px] flex-wrap gap-1.5">
                      {(teacher.assignments ?? []).map((assignment) => (
                        <span
                          key={assignment.classId}
                          className="inline-flex whitespace-nowrap rounded-full border border-accent-200 bg-accent-50 px-2.5 py-0.5 text-xs font-medium text-accent-900"
                        >
                          {classShortById[assignment.classId] ?? "—"} ·{" "}
                          {assignment.subjectIds
                            .map((id) => subjectNameById[id])
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <ContactCell teacher={teacher} />
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusCell teacher={teacher} />
                  </td>
                  <td className="px-5 py-3.5">
                    <RowActions teacher={teacher} onEdit={onEdit} onDelete={onDelete} onCreateAccount={onCreateAccount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-100" />
      </div>
    </section>
  );
}

function ClassChip({ classId }: { classId?: string }) {
  const { classShortById, classNameById } = useLabels();
  if (!classId) return <span className="text-xs text-foreground-400">—</span>;
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-100 px-3 py-0.5 text-xs font-semibold text-primary-800">
      <i className="ri-building-4-line text-xs"></i>
      {classShortById[classId] ?? classNameById[classId] ?? "—"}
    </span>
  );
}

function RowActions({
  teacher,
  onEdit,
  onDelete,
  onCreateAccount,
}: {
  teacher: Teacher;
  onEdit: (teacher: Teacher) => void;
  onDelete: (teacher: Teacher) => void;
  onCreateAccount?: (teacher: Teacher) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      {onCreateAccount && !teacher.userId && (
        <Button size="sm" variant="outline" icon="ri-user-add-line" onClick={() => onCreateAccount(teacher)}>
          Créer un compte
        </Button>
      )}
      <button
        type="button"
        onClick={() => onEdit(teacher)}
        aria-label={`Modifier ${teacher.firstName} ${teacher.lastName}`}
        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
      >
        <i className="ri-pencil-line"></i>
      </button>
      <button
        type="button"
        onClick={() => onDelete(teacher)}
        aria-label={`Supprimer ${teacher.firstName} ${teacher.lastName}`}
        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-accent-50 hover:text-accent-600 cursor-pointer"
      >
        <i className="ri-delete-bin-line"></i>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Page                                                                       */
/* ------------------------------------------------------------------------- */

interface ModalState {
  mode: "add" | "edit";
  form: TeacherFormState;
  editingId?: string;
}

function buildTeacher(form: TeacherFormState, primaryCycleId: string): Omit<Teacher, "id"> {
  const base = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    photo: form.photo || undefined,
    gender: form.gender,
    cycleId: form.cycleId,
    phone: form.phone.trim(),
    email: form.email.trim(),
    status: form.status,
    hireDate: form.hireDate,
    weeklyHours: form.weeklyHours ? Math.max(0, Math.round(Number(form.weeklyHours))) : 0,
    hourlyRate: form.hourlyRate ? Math.max(0, Math.round(Number(form.hourlyRate))) : 0,
    userId: form.userId,
  };
  if (form.cycleId === primaryCycleId) {
    const fixed = form.payMode === "FIXED" ? Math.round(Number(form.fixedMonthlySalary)) : 0;
    return { ...base, fixedMonthlySalary: fixed > 0 ? fixed : undefined, primaryClassId: form.primaryClassId, assignments: undefined };
  }
  return { ...base, fixedMonthlySalary: undefined, primaryClassId: undefined, assignments: form.assignments };
}

async function loadTeachersPage() {
  const [teachers, cycles, classes, subjects] = await Promise.all([
    getTeachers(),
    getCycles(),
    getClasses(),
    getSubjects(),
  ]);
  return { teachers, cycles, classes, subjects };
}

export default function TeachersPage() {
  const { notify } = useToast();
  const { data, loading, error, reload } = useAsync(loadTeachersPage, []);
  const { can } = useAuth();
  // Comptes de connexion (pour lier un enseignant à son compte) : visibles par qui gère les comptes.
  const { data: users } = useAsync(() => (can("users:read") ? listUsers() : Promise.resolve(null)), [can]);
  const teachers = useMemo(() => data?.teachers ?? [], [data]);
  const labels = useMemo(
    () => buildLabels(data?.cycles ?? [], data?.classes ?? [], data?.subjects ?? []),
    [data],
  );
  const { primaryCycleId: PRIMARY_CYCLE_ID, secondaryCycleId: SECONDARY_CYCLE_ID } = labels;
  const [search, setSearch] = useUrlSearch();
  const [cycleFilter, setCycleFilter] = useState("all");
  const [tab, setTab] = useState("liste");
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Teacher | null>(null);
  const [accountTarget, setAccountTarget] = useState<Teacher | null>(null);
  const [accountEmail, setAccountEmail] = useState("");
  const [accountBusy, setAccountBusy] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<TemporaryAccount | null>(null);

  const configuredCount = teachers.filter(isRated).length;

  const primaryCount = teachers.filter((t) => t.cycleId === PRIMARY_CYCLE_ID).length;
  const secondaryCount = teachers.filter((t) => t.cycleId === SECONDARY_CYCLE_ID).length;

  const term = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      teachers.filter((teacher) => {
        const matchCycle = cycleFilter === "all" || teacher.cycleId === cycleFilter;
        const matchTerm =
          !term ||
          `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(term) ||
          `${teacher.lastName} ${teacher.firstName}`.toLowerCase().includes(term);
        return matchCycle && matchTerm;
      }),
    [teachers, cycleFilter, term],
  );
  const primaryRows = filtered.filter((t) => t.cycleId === PRIMARY_CYCLE_ID);
  const secondaryRows = filtered.filter((t) => t.cycleId === SECONDARY_CYCLE_ID);

  const openCreate = () => {
    const defaultCycle = cycleFilter !== "all" ? cycleFilter : PRIMARY_CYCLE_ID;
    setModal({ mode: "add", form: createEmptyForm(defaultCycle) });
  };

  const openEdit = (teacher: Teacher) => {
    setModal({ mode: "edit", form: teacherToForm(teacher), editingId: teacher.id });
  };

  const handleSubmit = async (form: TeacherFormState) => {
    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`;
    const payload = buildTeacher(form, PRIMARY_CYCLE_ID);
    if (modal?.mode === "edit" && modal.editingId) {
      await updateTeacher(modal.editingId, payload);
      notify(`Enseignant « ${fullName} » mis à jour.`);
    } else {
      await addTeacher(payload);
      notify(`Enseignant « ${fullName} » ajouté.`);
    }
    setModal(null);
    reload();
  };

  const handleDelete = async (teacher: Teacher) => {
    await deleteTeacher(teacher.id);
    notify(`Enseignant « ${teacher.firstName} ${teacher.lastName} » supprimé.`, "info");
    setDeleteTarget(null);
    reload();
  };

  /** Le compte se crée en un clic ; sans e-mail sur la fiche, on le demande d'abord. */
  const requestAccount = (teacher: Teacher) => {
    if (teacher.email) void createAccount(teacher);
    else {
      setAccountEmail("");
      setAccountTarget(teacher);
    }
  };

  const createAccount = async (teacher: Teacher, email?: string) => {
    if (accountBusy) return;
    setAccountBusy(true);
    try {
      const created = await createTeacherAccount(teacher.id, email);
      setAccountTarget(null);
      setCreatedAccount(created);
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Création du compte impossible.", "error");
    } finally {
      setAccountBusy(false);
    }
  };

  const updateHours = async (
    id: string,
    patch: Partial<Pick<Teacher, "weeklyHours" | "hourlyRate">>,
  ) => {
    await updateTeacher(id, patch);
    reload();
  };

  if (loading && !data) return <LoadingState label="Chargement des enseignants..." />;
  if (error || !data) return <ErrorState message={error ?? "Erreur."} onRetry={reload} />;

  return (
    <LabelsContext.Provider value={labels}>
    <div className="animate-fade-in">
      <PageHeader
        title="Enseignants"
        subtitle={`${teachers.length} enseignants — ${primaryCount} primaire · ${secondaryCount} secondaire`}
        icon="ri-user-star-line"
        actions={
          tab === "liste" ? (
            <Button icon="ri-add-line" onClick={openCreate}>
              Ajouter un enseignant
            </Button>
          ) : (
            <Badge tone="secondary" icon="ri-timer-line">
              {configuredCount}/{teachers.length} tarifés
            </Badge>
          )
        }
      />

      <div className="mb-5">
        <Tabs
          tabs={[
            { id: "liste", label: "Liste des enseignants", icon: "ri-list-check-2", count: teachers.length },
            { id: "horaires", label: "Horaires & salaires", icon: "ri-timer-line", count: configuredCount },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === "liste" && (
      <>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un enseignant (prénom ou nom)..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="relative sm:w-56">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-filter-line text-sm"></i>
          </span>
          <select
            value={cycleFilter}
            onChange={(event) => setCycleFilter(event.target.value)}
            className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Tous les cycles</option>
            {data.cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-user-search-line text-2xl"></i>
          </span>
          <p className="text-sm text-foreground-600">
            Aucun enseignant ne correspond à votre recherche.
          </p>
        </div>
      ) : (
        <>
          {primaryRows.length > 0 && (
            <PrimaryTable
              rows={primaryRows}
              cycleLabel="Primaire"
              filterKey={`${search}|${cycleFilter}`}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onCreateAccount={can("users:create") ? requestAccount : undefined}
            />
          )}
          {secondaryRows.length > 0 && (
            <SecondaryTable
              rows={secondaryRows}
              cycleLabel="Secondaire"
              filterKey={`${search}|${cycleFilter}`}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onCreateAccount={can("users:create") ? requestAccount : undefined}
            />
          )}
        </>
      )}
      </>
      )}

      {tab === "horaires" && (
        <TeacherHoursTab teachers={teachers} cycles={data.cycles} onUpdate={updateHours} />
      )}

      <Modal
        open={accountTarget !== null}
        onClose={() => setAccountTarget(null)}
        title="Créer un compte"
        subtitle={accountTarget ? `${accountTarget.firstName} ${accountTarget.lastName}` : undefined}
        icon="ri-user-add-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setAccountTarget(null)}>
              Annuler
            </Button>
            <Button
              icon="ri-user-add-line"
              disabled={accountBusy || !accountEmail.trim()}
              onClick={() => accountTarget && void createAccount(accountTarget, accountEmail)}
            >
              {accountBusy ? "Création..." : "Créer le compte"}
            </Button>
          </>
        }
      >
        <Input
          label="E-mail de l'enseignant"
          type="email"
          placeholder="prenom.nom@ecole.gn"
          value={accountEmail}
          onChange={(event) => setAccountEmail(event.target.value)}
        />
        <p className="mt-2 text-xs text-foreground-500">
          Il sert d'identifiant de connexion et sera enregistré sur sa fiche.
        </p>
      </Modal>

      <TemporaryPasswordDialog
        admin={createdAccount}
        schoolName="Compte enseignant"
        onClose={() => setCreatedAccount(null)}
      />

      <TeacherFormModal
        open={modal !== null}
        mode={modal?.mode ?? "add"}
        form={modal?.form ?? createEmptyForm(PRIMARY_CYCLE_ID)}
        onChange={(form) => setModal((prev) => (prev ? { ...prev, form } : prev))}
        onClose={() => setModal(null)}
        onSubmit={handleSubmit}
        cycles={data.cycles}
        classes={data.classes}
        subjects={data.subjects}
        users={users ?? undefined}
        teachers={teachers}
        editingId={modal?.editingId}
      />

      <DeleteTeacherModal
        teacher={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
    </LabelsContext.Provider>
  );
}